const PRODUCES_RESPONSE_REGEX = /\[ProducesResponseType\(\s*typeof\(([^)]+)\)\s*,\s*StatusCodes\.(\w+)\s*\)\]/g;

/**
 * Extracts [ProducesResponseType(typeof(X), StatusCodes.Y)] attributes
 * declared directly above a given method, from RAW (unstripped) C# source.
 * CsClassParser.stripAttributes runs before any tokenizing/scanning, so this
 * data is otherwise gone by the time CsMemberScanner ever sees a method \u2014
 * this is a narrow, regex-based side-channel specifically for it, not a
 * general-purpose attribute parser.
 *
 * Finds the method by name, then scans BACKWARDS from there to the nearest
 * preceding `}` (end of whatever member/method comes before it) as a safe
 * window boundary, and pulls every ProducesResponseType attribute inside
 * that window, in the SAME top-to-bottom order they're declared.
 * @param {string} rawSource - unstripped file text
 * @param {string} methodName
 * @returns {{typeName: string, statusCode: string}[]}
 */
export function extractProducesResponses(rawSource, methodName) {
  const methodRegex = new RegExp(`\\b${methodName}\\s*\\(`);
  const match = methodRegex.exec(rawSource);
  if (!match) {
    return [];
  }

  const windowStart = Math.max(0, rawSource.lastIndexOf('}', match.index));
  const windowText = rawSource.slice(windowStart, match.index);

  const results = [];
  let attrMatch = PRODUCES_RESPONSE_REGEX.exec(windowText);
  while (attrMatch !== null) {
    results.push({ typeName: attrMatch[1].trim(), statusCode: attrMatch[2].trim() });
    attrMatch = PRODUCES_RESPONSE_REGEX.exec(windowText);
  }
  PRODUCES_RESPONSE_REGEX.lastIndex = 0; // reset the shared regex's exec state for the next call

  return results;
}

/**
 * Formats one ProducesResponseType entry as a display label, e.g.
 * "StatusCodes.Status202Accepted" -> "202 Accepted", plus the response type
 * if it's not the content-free EmptyResult (e.g. "200 OK: CmcFearSyncDto").
 * @param {{typeName: string, statusCode: string}} entry
 * @returns {string}
 */
export function formatProducesResponse({ typeName, statusCode }) {
  const humanStatus = statusCode.replace(/^Status/, '').replace(/(\d+)([A-Za-z])/, '$1 $2');
  if (typeName && typeName !== 'EmptyResult') {
    return `${humanStatus}: ${typeName}`;
  }
  return humanStatus;
}
